import React, { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Formik, useFormikContext } from "formik";
import { AppDispatch, RootState } from "redux/store";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import Input from "shared/Input/Input";
import Select from "shared/Select/Select";
import FormItem from "./FormItem";
import {
  getallupdateProperties,
  getRoomAmenitiesProperty,
  getRoomFeaturesProperty,
  getBedType,
  getRoomType,
  uploadImageProperties,
  deleteImage,
} from "../../redux/reducers/Properties/PropertiesSlice";
import successHandler from "utils/helpers/SuccessHandler";
import { roomRuleValidationSchema } from "utils/formSchema";
import CheckboxV2 from "shared/Checkbox/CheckboxV2";
import {
  BedType,
  RoomType,
} from "../../redux/reducers/Properties/PropertiesSliceTypes";
import CommonLayoutV2 from "./CommonLayoutV2";
// @ts-ignore
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import CustomDatePickerV2 from "shared/DateField/CustomDatePickerV2";
import { currencySymbol } from "shared/CurrencySymbol";
import { useDropzone } from "react-dropzone";
import NcImage from "shared/NcImage/NcImage";
import moment from "moment";
import { getIconComponent } from "shared/Icon/IconDymamiclisting";
import { showToastError } from "utils/helpers/ToastError";
import {
  calculateStepCompletion,
  checkExistingCompletion,
} from "utils/helpers/listingCompletionHelper";
import "react-loading-skeleton/dist/skeleton.css";

export interface RoomsProps {}

type RoomsAttributes = {
  rooms_attributes: RoomA[];
};

type RoomA = {
  id: string;
  name: string;
  price_per_month: string;
  status: string;
  availability_start: string;
  availability_end: string;
  description: string;
  bed_type_id: string;
  size: string;
  room_type_id: string;
  amenities: any[];
  features: any[];
};

const Rooms: FC<RoomsProps> = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { globalId, PropertyToEdit } = useSelector(
    (state: RootState) => state.property
  );
  const { slug } = useParams();
  const id = slug;
  const [isSharedProperty, setIsSharedProperty] = useState<boolean>(false);
  const [property, setProperty] = useState<any>(null);
  const [roomImagesToDelete, setRoomImagesToDelete] = useState<{
    [roomIndex: number]: number[];
  }>({});
  const [expandedRooms, setExpandedRooms] = useState<{
    [key: number]: boolean;
  }>({});

  // Track step completion status
  const [stepCompletionStatus, setStepCompletionStatus] = useState({
    1: { completed: false },
    2: { completed: false },
    3: { completed: false },
    4: { completed: false },
    5: { completed: false },
    6: { completed: false },
    7: { completed: false },
    8: { completed: false, fieldsCompleted: 0, totalFields: 1 },
    9: { completed: false },
  });

  const initialRoomValues = {
    id: "",
    name: "",
    price_per_month: "",
    status: "",
    availability_start: "",
    availability_end: "",
    description: "",
    bed_type_id: "",
    size: "",
    room_type_id: "",
    amenities: [],
    features: [],
    isSharedProperty: property?.is_shared_property,
    room_images: [],
    max_guests: "",
    bathroom_type: "",
  };

  const [initialValues, setInitialValues] = useState<any>({
    rooms_attributes: [initialRoomValues],
  });

  const [amenities, setAmenities] = useState<any>([]);
  const [features, setFeatures] = useState<any>([]);
  const [bedTypes, setBedTypes] = useState<BedType[]>([]);
  const [reorderImages, setReorderImages] = useState<any>([]);
  const [totalRooms, setTotalRooms] = useState<number | null>(null);
  const RouteID = id;
  const [roomTypes, setRoomTypes] = useState<RoomType[]>([]);
  const [count, setCount] = useState<number>(1);
  const [roomImagePreviews, setRoomImagePreviews] = React.useState<{
    [key: number]: string[];
  }>({});
  const [isLoading, setIsLoading] = useState(true);

  // Track form values for completion
  const [formValues, setFormValues] = useState<any>(null);
  const [formErrors, setFormErrors] = useState<any>({});

  useEffect(() => {
    if (initialValues?.rooms_attributes?.length > 0) {
      setCount(initialValues?.rooms_attributes?.length);
      // Expand first room by default
      setExpandedRooms({ 0: true });
    }
  }, [initialValues]);

  useEffect(() => {
    if (id) {
      const fetchPropertyData = async () => {
        const response: any = await dispatch(
          getallupdateProperties({ propertyId: id })
        );

        if (response.payload?.success) {
          setIsSharedProperty(response?.payload?.property?.is_shared_property);
          setProperty(response?.payload?.property);
        }

        setTotalRooms(response?.payload?.property?.total_bedrooms);
        if (response.payload?.property?.rooms) {
          const mappedRooms = response.payload.property.rooms.map(
            (room: any) => {
              const sortedPlaceImages = [...room?.room_images].sort(
                (a, b) => a.position - b.position
              );
              return {
                id: room.id,
                name: room.name,
                price_per_month:
                  room.price_per_month === "0.0"
                    ? ""
                    : room.price_per_month?.toString() || "",
                status: room.status,
                availability_start: room.availability_start
                  ? room.availability_start.split("T")[0]
                  : "",
                availability_end: room.availability_end
                  ? room.availability_end.split("T")[0]
                  : "",
                description: room.description || "",
                bed_type_id: room.bed_type?.id?.toString() || "",
                size: room.size?.toString() || "",
                room_type_id: room.room_type?.id?.toString() || "",
                amenities: room.amenities || [],
                features: room.features || [],
                isSharedProperty:
                  response?.payload?.property?.is_shared_property,
                room_images: [],
                existing_room_images: sortedPlaceImages || [],
                max_guests: room.max_guests?.toString() || "",
                bathroom_type: room.bathroom_type || "",
              };
            }
          );
          const uniqueRooms = Array.from(
            new Map(mappedRooms.map((room: any) => [room?.id, room])).values()
          );
          setInitialValues({
            rooms_attributes:
              uniqueRooms.length > 0
                ? uniqueRooms
                : initialValues.rooms_attributes?.map((item: any) => ({
                    ...item,
                    isSharedProperty:
                      response?.payload?.property?.is_shared_property || false,
                  })),
          });
        }
      };
      fetchPropertyData();
    }
  }, [dispatch, globalId, id]);

  // Calculate completion for Step 8
  useEffect(() => {
    if (formValues?.rooms_attributes) {
      // Check if at least one room is properly filled
      const hasValidRoom = formValues.rooms_attributes.some((room: any) => {
        return (
          room.name &&
          room.status &&
          room.bed_type_id &&
          room.size &&
          room.room_type_id
        );
      });

      const mockValues = { rooms: hasValidRoom ? "filled" : "" };
      const requiredFields = ["rooms"];
      const hasErrors = !hasValidRoom || Object.keys(formErrors).length > 0;

      const completion = calculateStepCompletion(
        mockValues,
        requiredFields,
        hasErrors
      );

      setStepCompletionStatus((prev) => ({
        ...prev,
        8: completion,
      }));
    }
  }, [formValues, formErrors]);

  // Load completion status from PropertyToEdit
  useEffect(() => {
    if (PropertyToEdit) {
      const existingCompletion = checkExistingCompletion(PropertyToEdit);

      setStepCompletionStatus((prev) => ({
        ...prev,
        ...existingCompletion,
      }));
    }
  }, [PropertyToEdit]);

  const handleAmenitiesChange = (
    amenityId: number,
    roomIndex: number,
    values: any,
    setFieldValue: any
  ) => {
    const updatedAmenities = values.rooms_attributes.map(
      (room: any, index: any) => {
        if (index === roomIndex) {
          const currentAmenities = room.amenities || [];
          const newAmenities = currentAmenities.some(
            (item: any) =>
              item.amenity_id === amenityId || item.id === amenityId
          )
            ? currentAmenities.filter(
                (item: any) =>
                  item.amenity_id !== amenityId && item.id !== amenityId
              )
            : [...currentAmenities, { amenity_id: amenityId }];
          return { ...room, amenities: newAmenities };
        }
        return room;
      }
    );
    setFieldValue("rooms_attributes", updatedAmenities);
  };

  const handleFeaturesChange = (
    featureId: number,
    roomIndex: number,
    values: any,
    setFieldValue: any
  ) => {
    const updatedFeatures = values.rooms_attributes.map(
      (room: any, index: any) => {
        if (index === roomIndex) {
          const currentFeatures = room.features || [];
          const newFeatures = currentFeatures.some(
            (item: any) =>
              item.feature_id === featureId || item.id === featureId
          )
            ? currentFeatures.filter(
                (item: any) =>
                  item.feature_id !== featureId && item.id !== featureId
              )
            : [...currentFeatures, { feature_id: featureId }];
          return { ...room, features: newFeatures };
        }
        return room;
      }
    );
    setFieldValue("rooms_attributes", updatedFeatures);
  };

  const handleSubmit = async (values: typeof initialValues) => {
    const formData = new FormData();

    if (id) {
      formData.append(`property[id]`, id);
    }

    const hasImagesToDelete = Object.values(roomImagesToDelete).some(
      (arr) => arr.length > 0
    );
    if (hasImagesToDelete) {
      try {
        const allImageIds: number[] = Object.values(roomImagesToDelete)
          .flatMap((ids) => ids)
          .filter((id): id is number => id !== undefined);
        if (allImageIds.length > 0) {
          await dispatch(deleteImage({ id: allImageIds })).unwrap();
        }
      } catch (error) {
        console.error("Error removing room images:", error);
      }
    }

    // Get property-level availability
    const propertyAvailabilityStart = property?.availability_start;
    const propertyAvailabilityEnd = property?.availability_end;
    const propertyUnlimitedAvailability = property?.unlimited_availability;

    // Calculate room availability dates based on property settings
    let roomAvailabilityStart = "";
    let roomAvailabilityEnd = "";

    if (!isSharedProperty) {
      // For Entire Property: automatically set room dates
      if (propertyAvailabilityStart) {
        roomAvailabilityStart = moment(propertyAvailabilityStart).format("YYYY-MM-DD");
        
        if (propertyUnlimitedAvailability) {
          // If unlimited, add 2 years to start date for rooms
          roomAvailabilityEnd = moment(propertyAvailabilityStart)
            .add(2, "years")
            .format("YYYY-MM-DD");
        } else if (propertyAvailabilityEnd) {
          // Use property's end date
          roomAvailabilityEnd = moment(propertyAvailabilityEnd).format("YYYY-MM-DD");
        }
      }
    }

    for (const [index, room] of values.rooms_attributes.entries()) {
      const modifiedAmenities = room.amenities?.map((item: any) => ({
        amenity_id: item.id || item.amenity_id,
      }));

      const modifiedFeatures = room.features?.map((item: any) => ({
        feature_id: item.id || item.feature_id,
      }));

      if (room.id) {
        formData.append(
          `property[rooms_attributes][${index}][id]`,
          room.id.toString()
        );
      }
      formData.append(`property[rooms_attributes][${index}][name]`, room.name);
      formData.append(
        `property[rooms_attributes][${index}][price_per_month]`,
        room.price_per_month ? room.price_per_month.toString() : ""
      );
      formData.append(
        `property[rooms_attributes][${index}][status]`,
        room.status
      );
      
      // Set availability based on property type
      if (isSharedProperty) {
        // Shared Property: Use room-specific dates from user input
        formData.append(
          `property[rooms_attributes][${index}][availability_start]`,
          moment(room.availability_start).format("YYYY-MM-DD") || ""
        );
        formData.append(
          `property[rooms_attributes][${index}][availability_end]`,
          moment(room.availability_end).format("YYYY-MM-DD") || ""
        );
      } else {
        // Entire Property: Use automatically calculated dates
        formData.append(
          `property[rooms_attributes][${index}][availability_start]`,
          roomAvailabilityStart
        );
        formData.append(
          `property[rooms_attributes][${index}][availability_end]`,
          roomAvailabilityEnd
        );
      }

      formData.append(
        `property[rooms_attributes][${index}][description]`,
        room.description
      );
      formData.append(
        `property[rooms_attributes][${index}][bed_type_id]`,
        room.bed_type_id.toString()
      );
      formData.append(
        `property[rooms_attributes][${index}][size]`,
        room.size.toString()
      );
      formData.append(
        `property[rooms_attributes][${index}][room_type_id]`,
        room.room_type_id.toString()
      );
      formData.append(
        `property[rooms_attributes][${index}][max_guests]`,
        room.max_guests.toString()
      );

      formData.append(
        `property[rooms_attributes][${index}][bathroom_type]`,
        room.bathroom_type || ""
      );

      if (reorderImages?.length > 0) {
        reorderImages?.forEach((item: any) => {
          formData.append(
            "property[room_image_metadata][][blob_id]",
            item.blob_id
          );
          formData.append(
            "property[room_image_metadata][][position]",
            item.position
          );
        });
      }

      if (modifiedAmenities) {
        modifiedAmenities.forEach((amenity: any, amenityIndex: number) => {
          formData.append(
            `property[rooms_attributes][${index}][room_amenities_attributes][${amenityIndex}][amenity_id]`,
            amenity.amenity_id
          );
        });
      }

      if (modifiedFeatures) {
        modifiedFeatures.forEach((feature: any, featureIndex: number) => {
          formData.append(
            `property[rooms_attributes][${index}][room_features_attributes][${featureIndex}][feature_id]`,
            feature.feature_id
          );
        });
      }

      const newRoomImages = room.room_images?.filter(
        (image: any) => image instanceof File
      );
      const existingImages = room.existing_room_images || [];

      if (
        (newRoomImages && newRoomImages.length > 0) ||
        existingImages.length > 0
      ) {
        if (newRoomImages && newRoomImages.length > 0) {
          newRoomImages.forEach((image: File) => {
            if (image instanceof File) {
              formData.append(
                `property[rooms_attributes][${index}][room_images][]`,
                image
              );
            }
          });
        }

        if (existingImages.length > 0) {
          existingImages.forEach((imageUrl: string) => {
            if (imageUrl && typeof imageUrl === "string") {
              formData.append(
                `property[rooms_attributes][${index}][room_images][]`,
                imageUrl
              );
            }
          });
        }
      }
    }

    try {
      const response = await dispatch(uploadImageProperties(formData)).unwrap();
      if (response) {
        successHandler(response?.message || "Updated successfully");
        navigate(`/add-listing-9/${slug}`);
      }
    } catch (error) {
      console.error("Error updating property: ", error);
    }
  };

  const handleRoomImagesChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
    setFieldValue: any,
    values: any
  ) => {
    const files = e.target.files;
    const newFiles = files ? Array.from(files) : [];

    if (files) {
      const newPreviews = newFiles.map((file) => URL.createObjectURL(file));

      setRoomImagePreviews((prevPreviews) => ({
        ...prevPreviews,
        [index]: [...(prevPreviews[index] || []), ...newPreviews],
      }));

      const existingImages =
        values.rooms_attributes?.[index]?.room_images || [];
      const combinedImages = [...existingImages, ...newFiles];
      setFieldValue(`rooms_attributes[${index}].room_images`, combinedImages);
    }
  };

  const handleRemoveRoomImage = (
    previewIndex: number,
    index: number,
    values: any,
    setFieldValue: any
  ) => {
    const existingImages =
      values?.rooms_attributes[index]?.existing_room_images || [];
    const isExistingImage = previewIndex < existingImages.length;

    if (isExistingImage) {
      const imageIdToRemove = existingImages[previewIndex]?.id;
      setRoomImagesToDelete((prev) => {
        const updatedMap = { ...prev };
        if (!updatedMap[index]) {
          updatedMap[index] = [];
        }
        updatedMap[index] = [...updatedMap[index], imageIdToRemove];
        return updatedMap;
      });
      const updatedExistingImages = existingImages.filter(
        (_: any, idx: number) => idx !== previewIndex
      );
      setFieldValue(
        `rooms_attributes[${index}].existing_room_images`,
        updatedExistingImages
      );
    } else {
      const newPreviews = roomImagePreviews[index] || [];
      const updatedPreviews = newPreviews.filter(
        (_: string, idx: number) => idx !== previewIndex - existingImages.length
      );

      setRoomImagePreviews((prevPreviews) => {
        const updatedPreviewsMap = { ...prevPreviews };
        updatedPreviewsMap[index] = updatedPreviews;
        return updatedPreviewsMap;
      });

      const newImages = values.rooms_attributes[index]?.room_images || [];
      const updatedNewImages = newImages.filter(
        (_: any, idx: number) => idx !== previewIndex - existingImages.length
      );
      setFieldValue(`rooms_attributes[${index}].room_images`, updatedNewImages);
    }
  };

  useEffect(() => {
    dispatch(getRoomFeaturesProperty())
      .unwrap()
      .then((response) => setFeatures(response.data))
      .catch((error) => console.error("Error fetching features:", error));

    dispatch(getRoomAmenitiesProperty())
      .unwrap()
      .then((response) => setAmenities(response.data))
      .catch((error) => console.error("Error fetching features:", error));
  }, [dispatch]);

  useEffect(() => {
    const fetchBedTypes = async () => {
      try {
        const response = await dispatch(getBedType()).unwrap();
        if (response.success) {
          setBedTypes(response.data);
        }
      } catch (error) {
        console.error("Error fetching bed types:", error);
      }
    };

    const fetchRoomTypes = async () => {
      try {
        const response = await dispatch(getRoomType()).unwrap();
        if (response.success) {
          setRoomTypes(response.data);
        }
      } catch (error) {
        console.error("Error fetching room types:", error);
      }
    };

    fetchBedTypes();
    fetchRoomTypes();
  }, [dispatch]);

  const useRoomsImagesDropzone = (
    index: number,
    values: any,
    setFieldValue: Function
  ) => {
    return useDropzone({
      onDrop: (acceptedFiles) => {
        const files = acceptedFiles.map((file) => URL.createObjectURL(file));

        setRoomImagePreviews((prevPreviews) => ({
          ...prevPreviews,
          [index]: [...(prevPreviews[index] || []), ...files],
        }));

        const existingImages =
          values.rooms_attributes?.[index]?.room_images || [];
        const combinedImages = [...existingImages, ...acceptedFiles];
        setFieldValue(`rooms_attributes[${index}].room_images`, combinedImages);
      },
    });
  };

  const RoomImagesUploader = ({ index }: { index: number }) => {
    const { values, setFieldValue } = useFormikContext<any>();

    const {
      getRootProps: getRoomsImagesRootProps,
      getInputProps: getRoomsImagesInputProps,
    } = useRoomsImagesDropzone(index, values, setFieldValue);

    return (
      <div
        {...getRoomsImagesRootProps()}
        className="mt-4 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-xl hover:border-[#F56948] transition-colors cursor-pointer"
      >
        <input
          {...getRoomsImagesInputProps()}
          id={`room-image-upload-${index}`}
          name={`room-image-upload-${index}`}
          type="file"
          className="sr-only"
          onChange={(e) =>
            handleRoomImagesChange(e, index, setFieldValue, values)
          }
        />
        <div className="space-y-1 text-center">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            stroke="currentColor"
            fill="none"
            viewBox="0 0 48 48"
            aria-hidden="true"
          >
            <path
              d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            ></path>
          </svg>
          <div className="flex text-sm text-gray-600 dark:text-gray-300">
            <span className="relative cursor-pointer rounded-md font-medium text-[#F56948] hover:text-[#E5593D]">
              <span>Upload room images</span>
            </span>
            <p className="pl-1">or drag and drop</p>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            PNG, JPG, GIF up to 10MB
          </p>
        </div>
      </div>
    );
  };

  useEffect(() => {
    setTimeout(() => setIsLoading(false), 1500);
  }, []);

  const toggleRoomExpansion = (index: number) => {
    setExpandedRooms((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  return (
    <CommonLayoutV2
      currentHref="/add-listing-8"
      PropertyID={RouteID}
      propertyType={PropertyToEdit?.property_type?.name}
      stepCompletionStatus={stepCompletionStatus}
    >
      <div className="space-y-8">
        {/* Header Section */}
        <div className="pb-6 border-b border-neutral-200 dark:border-neutral-700">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Room Details
          </h2>
          <p className="text-gray-600 dark:text-gray-400 text-sm">
            Provide detailed information about each room in your property.{" "}
            {totalRooms && `You can add up to ${totalRooms} rooms.`}
          </p>
        </div>

        <Formik
          initialValues={initialValues}
          validationSchema={roomRuleValidationSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {({
            errors,
            values,
            handleChange,
            handleSubmit,
            isSubmitting,
            getFieldProps,
            setFieldValue,
            validateForm,
          }) => {
            // Update form tracking
            if (JSON.stringify(values) !== JSON.stringify(formValues)) {
              setFormValues(values);
            }
            if (JSON.stringify(errors) !== JSON.stringify(formErrors)) {
              setFormErrors(errors);
            }

            const formatFieldName = (field: string): string => {
              return field
                .replace(/_/g, " ")
                .replace(/\b\w/g, (char) => char.toUpperCase());
            };

            const handleFormSubmit = async () => {
              const formErrors = await validateForm();
              if (Object.keys(formErrors).length > 0) {
                if (
                  formErrors.rooms_attributes &&
                  Array.isArray(formErrors.rooms_attributes)
                ) {
                  for (
                    let index = 0;
                    index < formErrors.rooms_attributes.length;
                    index++
                  ) {
                    const roomError = formErrors.rooms_attributes[index];
                    if (roomError && typeof roomError === "object") {
                      for (const field of Object.keys(roomError)) {
                        if (roomError[field]) {
                          showToastError(
                            `Room ${index + 1} - ${roomError[field]}`
                          );
                          return;
                        }
                      }
                    }
                  }
                }
                for (const key of Object.keys(formErrors)) {
                  if (key !== "rooms_attributes") {
                    const error = formErrors[key];
                    if (typeof error === "string") {
                      const formattedKey = formatFieldName(key);
                      showToastError(`${formattedKey}: ${error}`);
                      return;
                    }
                  }
                }
                return;
              }
              handleSubmit();
            };

            return (
              <form onSubmit={handleSubmit} className="space-y-6">
                {values.rooms_attributes.map((room: any, index: number) => {
                  const isExpanded = expandedRooms[index] ?? false;
                  const hasErrors =
                    errors.rooms_attributes &&
                    Array.isArray(errors.rooms_attributes) &&
                    errors.rooms_attributes[index];

                  return (
                    <div
                      key={index}
                      className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden"
                    >
                      {/* Room Header - Always Visible */}
                      <div
                        className="flex items-center justify-between p-6 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                        onClick={() => toggleRoomExpansion(index)}
                      >
                        <div className="flex items-center gap-4">
                          <span className="w-10 h-10 rounded-full bg-[#F56948] text-white flex items-center justify-center text-base font-bold flex-shrink-0">
                            {index + 1}
                          </span>
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                              {room.name || `Room ${index + 1}`}
                            </h3>
                            {room.room_type_id && !isExpanded && (
                              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                {roomTypes.find(
                                  (rt) => rt.id.toString() === room.room_type_id
                                )?.name || ""}
                                {room.size && ` • ${room.size} sq ft`}
                                {room.status && ` • ${room.status}`}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {hasErrors && (
                            <span className="bg-red-100 text-red-600 dark:bg-red-900/20 dark:text-red-400 px-3 py-1 rounded-full text-xs font-medium">
                              Incomplete
                            </span>
                          )}
                          <button
                            type="button"
                            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                          >
                            <svg
                              className={`w-6 h-6 transition-transform ${
                                isExpanded ? "rotate-180" : ""
                              }`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>

                      {/* Room Content - Collapsible */}
                      {isExpanded && (
                        <div className="p-6 pt-0 space-y-6 border-t border-gray-100 dark:border-gray-700">
                          {/* Basic Info Section */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <FormItem label="Room Name *">
                              <Input
                                type="text"
                                placeholder="e.g., Master Bedroom"
                                {...getFieldProps(
                                  `rooms_attributes[${index}].name`
                                )}
                                className="text-base"
                              />
                              {errors.rooms_attributes &&
                                Array.isArray(errors.rooms_attributes) &&
                                typeof errors.rooms_attributes[index] ===
                                  "object" &&
                                errors.rooms_attributes[index] !== null &&
                                (
                                  errors.rooms_attributes[index] as {
                                    [key: string]: string;
                                  }
                                ).name && (
                                  <div className="text-red-500 text-sm mt-2 flex items-center gap-1">
                                    <svg
                                      className="w-4 h-4"
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                    {
                                      (
                                        errors.rooms_attributes[index] as {
                                          [key: string]: string;
                                        }
                                      ).name
                                    }
                                  </div>
                                )}
                            </FormItem>

                            <FormItem label="Status *">
                              <Select
                                name={`rooms_attributes[${index}].status`}
                                onChange={handleChange}
                                value={room.status}
                                className="text-base"
                              >
                                <option value="">Select status</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                              </Select>
                              {errors.rooms_attributes &&
                                Array.isArray(errors.rooms_attributes) &&
                                typeof errors.rooms_attributes[index] ===
                                  "object" &&
                                errors.rooms_attributes[index] !== null &&
                                (
                                  errors.rooms_attributes[index] as {
                                    [key: string]: string;
                                  }
                                ).status && (
                                  <div className="text-red-500 text-sm mt-2 flex items-center gap-1">
                                    <svg
                                      className="w-4 h-4"
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                    {
                                      (
                                        errors.rooms_attributes[index] as {
                                          [key: string]: string;
                                        }
                                      ).status
                                    }
                                  </div>
                                )}
                            </FormItem>
                          </div>

                          {/* Shared Property Fields */}
                          {isSharedProperty && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <FormItem label="Price per Month *">
                                <div className="relative">
                                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 font-medium">
                                    {currencySymbol()}
                                  </span>
                                  <Input
                                    type="text"
                                    name={`rooms_attributes[${index}].price_per_month`}
                                    placeholder="0.00"
                                    onChange={handleChange}
                                    value={
                                      values.rooms_attributes[index]
                                        ?.price_per_month
                                    }
                                    className="pl-8 text-base"
                                  />
                                </div>
                                {errors.rooms_attributes &&
                                  Array.isArray(errors.rooms_attributes) &&
                                  typeof errors.rooms_attributes[index] ===
                                    "object" &&
                                  errors.rooms_attributes[index] !== null &&
                                  (
                                    errors.rooms_attributes[index] as {
                                      [key: string]: string;
                                    }
                                  ).price_per_month && (
                                    <div className="text-red-500 text-sm mt-2 flex items-center gap-1">
                                      <svg
                                        className="w-4 h-4"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                      >
                                        <path
                                          fillRule="evenodd"
                                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                                          clipRule="evenodd"
                                        />
                                      </svg>
                                      {
                                        (
                                          errors.rooms_attributes[index] as {
                                            [key: string]: string;
                                          }
                                        ).price_per_month
                                      }
                                    </div>
                                  )}
                              </FormItem>

                              <FormItem label="Max Guests *">
                                <Input
                                  type="text"
                                  name={`rooms_attributes[${index}].max_guests`}
                                  placeholder="e.g., 2"
                                  onChange={handleChange}
                                  value={
                                    values.rooms_attributes[index]?.max_guests
                                  }
                                  className="text-base"
                                />
                                {errors.rooms_attributes &&
                                  Array.isArray(errors.rooms_attributes) &&
                                  typeof errors.rooms_attributes[index] ===
                                    "object" &&
                                  errors.rooms_attributes[index] !== null &&
                                  (
                                    errors.rooms_attributes[index] as {
                                      [key: string]: string;
                                    }
                                  ).max_guests && (
                                    <div className="text-red-500 text-sm mt-2 flex items-center gap-1">
                                      <svg
                                        className="w-4 h-4"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                      >
                                        <path
                                          fillRule="evenodd"
                                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                                          clipRule="evenodd"
                                        />
                                      </svg>
                                      {
                                        (
                                          errors.rooms_attributes[index] as {
                                            [key: string]: string;
                                          }
                                        ).max_guests
                                      }
                                    </div>
                                  )}
                              </FormItem>
                            </div>
                          )}

                          {/* Availability Dates for Shared Property */}
                          {isSharedProperty && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <FormItem label="Availability Start *">
                                <CustomDatePickerV2
                                  placeholder="Select start date"
                                  value={
                                    room?.availability_start
                                      ? moment(room?.availability_start)
                                      : null
                                  }
                                  onChange={(value) => {
                                    setFieldValue(
                                      `rooms_attributes[${index}].availability_start`,
                                      value
                                    );
                                    setFieldValue(
                                      `rooms_attributes[${index}].availability_end`,
                                      null
                                    );
                                  }}
                                  minDate={
                                    property?.availability_start
                                      ? moment(property?.availability_start)
                                          .startOf("day")
                                          .toDate()
                                      : new Date()
                                  }
                                  maxDate={
                                    property?.availability_end
                                      ? moment(property?.availability_end)
                                          .endOf("day")
                                          .toDate()
                                      : undefined
                                  }
                                />
                                {errors.rooms_attributes &&
                                  Array.isArray(errors.rooms_attributes) &&
                                  errors.rooms_attributes[index] &&
                                  typeof errors.rooms_attributes[index] ===
                                    "object" &&
                                  errors.rooms_attributes[index] !== null &&
                                  errors.rooms_attributes[index]
                                    .booking_start && (
                                    <div className="text-red-500 text-sm mt-2 flex items-center gap-1">
                                      <svg
                                        className="w-4 h-4"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                      >
                                        <path
                                          fillRule="evenodd"
                                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                                          clipRule="evenodd"
                                        />
                                      </svg>
                                      {typeof errors.rooms_attributes[index]
                                        .booking_start === "string" &&
                                        errors.rooms_attributes[index]
                                          .booking_start}
                                    </div>
                                  )}
                              </FormItem>

                              <FormItem label="Availability End *">
                                <CustomDatePickerV2
                                  placeholder="Select end date"
                                  value={
                                    room?.availability_end
                                      ? moment(room?.availability_end)
                                      : null
                                  }
                                  onChange={(value) => {
                                    setFieldValue(
                                      `rooms_attributes[${index}].availability_end`,
                                      value
                                    );
                                  }}
                                  minDate={
                                    room?.availability_start
                                      ? moment(room?.availability_start)
                                          .add(1, "day")
                                          .startOf("day")
                                          .toDate()
                                      : property?.availability_start
                                      ? moment(property?.availability_start)
                                          .startOf("day")
                                          .toDate()
                                      : new Date()
                                  }
                                  maxDate={
                                    property?.availability_end
                                      ? moment(property?.availability_end)
                                          .endOf("day")
                                          .toDate()
                                      : undefined
                                  }
                                />
                                {errors.rooms_attributes &&
                                  Array.isArray(errors.rooms_attributes) &&
                                  errors.rooms_attributes[index] &&
                                  typeof errors.rooms_attributes[index] ===
                                    "object" &&
                                  errors.rooms_attributes[index] !== null &&
                                  errors.rooms_attributes[index]
                                    .booking_end && (
                                    <div className="text-red-500 text-sm mt-2 flex items-center gap-1">
                                      <svg
                                        className="w-4 h-4"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                      >
                                        <path
                                          fillRule="evenodd"
                                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                                          clipRule="evenodd"
                                        />
                                      </svg>
                                      {typeof errors.rooms_attributes[index]
                                        .booking_end === "string" &&
                                        errors.rooms_attributes[index]
                                          .booking_end}
                                    </div>
                                  )}
                              </FormItem>
                            </div>
                          )}

                          {/* Description */}
                          <FormItem label="Description *">
                            <textarea
                              className="block w-full h-24 px-4 py-3 text-sm rounded-xl border-2 border-gray-200 dark:border-gray-600 focus:border-[#F56948] dark:focus:border-[#F56948] bg-white dark:bg-gray-800"
                              name={`rooms_attributes[${index}].description`}
                              placeholder="Describe this room's features, layout, and amenities..."
                              onChange={handleChange}
                              value={room.description}
                            />
                            {errors.rooms_attributes &&
                              Array.isArray(errors.rooms_attributes) &&
                              typeof errors.rooms_attributes[index] ===
                                "object" &&
                              errors.rooms_attributes[index] !== null &&
                              (
                                errors.rooms_attributes[index] as {
                                  [key: string]: string;
                                }
                              ).description && (
                                <div className="text-red-500 text-sm mt-2 flex items-center gap-1">
                                  <svg
                                    className="w-4 h-4"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                  {
                                    (
                                      errors.rooms_attributes[index] as {
                                        [key: string]: string;
                                      }
                                    ).description
                                  }
                                </div>
                              )}
                          </FormItem>

                          {/* Room Specifications - Reorganized for Better Layout */}
                          <div className="space-y-4">
                            {/* Row 1: Bed Type and Room Type */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <FormItem label="Bed Type *">
                                <Select
                                  name={`rooms_attributes[${index}].bed_type_id`}
                                  onChange={handleChange}
                                  value={room.bed_type_id}
                                  className="text-base"
                                >
                                  <option value="">Select bed type</option>
                                  {bedTypes.map((bedType) => (
                                    <option key={bedType.id} value={bedType.id}>
                                      {bedType.name}
                                    </option>
                                  ))}
                                </Select>
                                {errors.rooms_attributes &&
                                  Array.isArray(errors.rooms_attributes) &&
                                  typeof errors.rooms_attributes[index] ===
                                    "object" &&
                                  errors.rooms_attributes[index] !== null &&
                                  (
                                    errors.rooms_attributes[index] as {
                                      [key: string]: string;
                                    }
                                  ).bed_type_id && (
                                    <div className="text-red-500 text-sm mt-2 flex items-center gap-1">
                                      <svg
                                        className="w-4 h-4"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                      >
                                        <path
                                          fillRule="evenodd"
                                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                                          clipRule="evenodd"
                                        />
                                      </svg>
                                      {
                                        (
                                          errors.rooms_attributes[index] as {
                                            [key: string]: string;
                                          }
                                        ).bed_type_id
                                      }
                                    </div>
                                  )}
                              </FormItem>

                              <FormItem label="Room Type *">
                                <Select
                                  name={`rooms_attributes[${index}].room_type_id`}
                                  onChange={handleChange}
                                  value={room.room_type_id}
                                  className="text-base"
                                >
                                  <option value="">Select room type</option>
                                  {roomTypes.map((roomType) => (
                                    <option
                                      key={roomType.id}
                                      value={roomType.id}
                                    >
                                      {roomType.name}
                                    </option>
                                  ))}
                                </Select>
                                {errors.rooms_attributes &&
                                  Array.isArray(errors.rooms_attributes) &&
                                  typeof errors.rooms_attributes[index] ===
                                    "object" &&
                                  errors.rooms_attributes[index] !== null &&
                                  (
                                    errors.rooms_attributes[index] as {
                                      [key: string]: string;
                                    }
                                  ).room_type_id && (
                                    <div className="text-red-500 text-sm mt-2 flex items-center gap-1">
                                      <svg
                                        className="w-4 h-4"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                      >
                                        <path
                                          fillRule="evenodd"
                                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                                          clipRule="evenodd"
                                        />
                                      </svg>
                                      {
                                        (
                                          errors.rooms_attributes[index] as {
                                            [key: string]: string;
                                          }
                                        ).room_type_id
                                      }
                                    </div>
                                  )}
                              </FormItem>
                            </div>

                            {/* Row 2: Bathroom Type and Room Size */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <FormItem label="Bathroom Type *">
                                <Select
                                  name={`rooms_attributes[${index}].bathroom_type`}
                                  onChange={handleChange}
                                  value={room.bathroom_type}
                                  className="text-base"
                                >
                                  <option value="">Select bathroom type</option>
                                  <option value="ensuite_bathroom">
                                    Ensuite Bathroom
                                  </option>
                                  <option value="external_private_bathroom">
                                    External Private Bathroom
                                  </option>
                                  <option value="shared_bathroom">
                                    Shared Bathroom
                                  </option>
                                </Select>
                                {errors.rooms_attributes &&
                                  Array.isArray(errors.rooms_attributes) &&
                                  typeof errors.rooms_attributes[index] ===
                                    "object" &&
                                  errors.rooms_attributes[index] !== null &&
                                  (
                                    errors.rooms_attributes[index] as {
                                      [key: string]: string;
                                    }
                                  ).bathroom_type && (
                                    <div className="text-red-500 text-sm mt-2 flex items-center gap-1">
                                      <svg
                                        className="w-4 h-4"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                      >
                                        <path
                                          fillRule="evenodd"
                                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                                          clipRule="evenodd"
                                        />
                                      </svg>
                                      {
                                        (
                                          errors.rooms_attributes[index] as {
                                            [key: string]: string;
                                          }
                                        ).bathroom_type
                                      }
                                    </div>
                                  )}
                              </FormItem>

                              <FormItem label="Room Size (sq ft) *">
                                <Input
                                  type="text"
                                  name={`rooms_attributes[${index}].size`}
                                  placeholder="e.g., 150"
                                  onChange={handleChange}
                                  value={room.size}
                                  className="text-base"
                                />
                                {errors.rooms_attributes &&
                                  Array.isArray(errors.rooms_attributes) &&
                                  typeof errors.rooms_attributes[index] ===
                                    "object" &&
                                  errors.rooms_attributes[index] !== null &&
                                  (
                                    errors.rooms_attributes[index] as {
                                      [key: string]: string;
                                    }
                                  ).size && (
                                    <div className="text-red-500 text-sm mt-2 flex items-center gap-1">
                                      <svg
                                        className="w-4 h-4"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                      >
                                        <path
                                          fillRule="evenodd"
                                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                                          clipRule="evenodd"
                                        />
                                      </svg>
                                      {
                                        (
                                          errors.rooms_attributes[index] as {
                                            [key: string]: string;
                                          }
                                        ).size
                                      }
                                    </div>
                                  )}
                              </FormItem>
                            </div>
                          </div>

                          {/* NEW: Availability Info for Entire Property */}
                          {!isSharedProperty && property?.availability_start && (
                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-2 border-blue-200 dark:border-blue-700 rounded-xl p-5">
                              <div className="flex gap-4">
                                <div className="flex-shrink-0">
                                  <svg 
                                    className="w-6 h-6 text-blue-600 dark:text-blue-400" 
                                    fill="currentColor" 
                                    viewBox="0 0 20 20"
                                  >
                                    <path 
                                      fillRule="evenodd" 
                                      d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" 
                                      clipRule="evenodd" 
                                    />
                                  </svg>
                                </div>
                                <div className="flex-1">
                                  <h4 className="text-base font-semibold text-blue-900 dark:text-blue-200 mb-2 flex items-center gap-2">
                                    Room Availability (Auto-Set)
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-600 text-white">
                                      Entire Property
                                    </span>
                                  </h4>
                                  <div className="space-y-2 text-sm text-blue-800 dark:text-blue-300">
                                    <p>
                                      Since this is an <strong>Entire Property</strong> listing, room availability is automatically set based on the property's availability calendar.
                                    </p>
                                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 mt-3 border border-blue-200 dark:border-blue-700">
                                      <div className="flex items-center gap-2 mb-2">
                                        <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                        <span className="font-semibold text-gray-900 dark:text-white">Auto-configured dates:</span>
                                      </div>
                                      <ul className="space-y-1.5 ml-6">
                                        <li className="flex items-center gap-2">
                                          <span className="text-gray-600 dark:text-gray-400">Start:</span>
                                          <strong className="text-gray-900 dark:text-white">
                                            {moment(property.availability_start).format("MMMM D, YYYY")}
                                          </strong>
                                        </li>
                                        <li className="flex items-center gap-2">
                                          <span className="text-gray-600 dark:text-gray-400">End:</span>
                                          <strong className="text-gray-900 dark:text-white">
                                            {property.unlimited_availability ? (
                                              <>
                                                {moment(property.availability_start).add(2, "years").format("MMMM D, YYYY")}
                                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
                                                  2 years from start
                                                </span>
                                              </>
                                            ) : property.availability_end ? (
                                              moment(property.availability_end).format("MMMM D, YYYY")
                                            ) : (
                                              "Not set"
                                            )}
                                          </strong>
                                        </li>
                                      </ul>
                                    </div>
                                    {property.unlimited_availability && (
                                      <p className="text-xs mt-3 flex items-start gap-2 bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg border border-purple-200 dark:border-purple-700">
                                        <svg className="w-4 h-4 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                        </svg>
                                        <span className="text-purple-800 dark:text-purple-300">
                                          <strong>Note:</strong> Your property has unlimited availability. For room records, we automatically set an end date 2 years from the start date. This can be extended as needed.
                                        </span>
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Amenities Section - Compact Grid */}
                          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                            <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-4">
                              Room Amenities
                              {values.rooms_attributes[index]?.amenities
                                ?.length > 0 && (
                                <span className="ml-2 bg-[#F56948] text-white px-2 py-1 rounded-full text-xs font-semibold">
                                  {
                                    values.rooms_attributes[index]?.amenities
                                      ?.length
                                  }
                                </span>
                              )}
                            </h4>
                            {amenities?.length === 0 ? (
                              <p className="text-gray-500 text-sm">
                                No amenities available
                              </p>
                            ) : (
                              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                                {amenities.map((amenity: any) => {
                                  const isChecked = values.rooms_attributes[
                                    index
                                  ]?.amenities?.some(
                                    (item: any) =>
                                      item.amenity_id === amenity.id ||
                                      item.id === amenity.id
                                  );
                                  return (
                                    <label
                                      key={amenity.id}
                                      className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                        isChecked
                                          ? "border-[#F56948] bg-orange-50 dark:bg-orange-900/10"
                                          : "border-gray-200 dark:border-gray-700 hover:border-gray-300"
                                      }`}
                                    >
                                      <input
                                        type="checkbox"
                                        className="sr-only"
                                        checked={isChecked}
                                        onChange={() =>
                                          handleAmenitiesChange(
                                            amenity?.id,
                                            index,
                                            values,
                                            setFieldValue
                                          )
                                        }
                                      />
                                      <div
                                        className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                                          isChecked
                                            ? "bg-[#F56948] border-[#F56948]"
                                            : "border-gray-300"
                                        }`}
                                      >
                                        {isChecked && (
                                          <svg
                                            className="w-3 h-3 text-white"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth={3}
                                              d="M5 13l4 4L19 7"
                                            />
                                          </svg>
                                        )}
                                      </div>
                                      <span className="text-xs font-medium text-gray-900 dark:text-white">
                                        {amenity.name}
                                      </span>
                                    </label>
                                  );
                                })}
                              </div>
                            )}
                          </div>

                          {/* Features Section - Compact Grid */}
                          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                            <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-4">
                              Room Features
                              {values.rooms_attributes[index]?.features
                                ?.length > 0 && (
                                <span className="ml-2 bg-gray-300 dark:bg-gray-600 text-gray-900 dark:text-white px-2 py-1 rounded-full text-xs font-semibold">
                                  {
                                    values.rooms_attributes[index]?.features
                                      ?.length
                                  }
                                </span>
                              )}
                            </h4>
                            {features?.length === 0 ? (
                              <p className="text-gray-500 text-sm">
                                No features available
                              </p>
                            ) : (
                              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                                {features.map((feature: any) => {
                                  const isChecked = values.rooms_attributes[
                                    index
                                  ]?.features?.some(
                                    (item: any) =>
                                      item.feature_id === feature.id ||
                                      item.id === feature.id
                                  );
                                  return (
                                    <label
                                      key={feature.id}
                                      className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                        isChecked
                                          ? "border-gray-400 bg-gray-50 dark:bg-gray-700/50"
                                          : "border-gray-200 dark:border-gray-700 hover:border-gray-300"
                                      }`}
                                    >
                                      <input
                                        type="checkbox"
                                        className="sr-only"
                                        checked={isChecked}
                                        onChange={() =>
                                          handleFeaturesChange(
                                            feature?.id,
                                            index,
                                            values,
                                            setFieldValue
                                          )
                                        }
                                      />
                                      <div
                                        className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                                          isChecked
                                            ? "bg-gray-600 border-gray-600"
                                            : "border-gray-300"
                                        }`}
                                      >
                                        {isChecked && (
                                          <svg
                                            className="w-3 h-3 text-white"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth={3}
                                              d="M5 13l4 4L19 7"
                                            />
                                          </svg>
                                        )}
                                      </div>
                                      <span className="text-xs font-medium text-gray-900 dark:text-white">
                                        {feature.name}
                                      </span>
                                    </label>
                                  );
                                })}
                              </div>
                            )}
                          </div>

                          {/* Room Images Section */}
                          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                            <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-4">
                              Room Images
                            </h4>
                            <RoomImagesUploader index={index} />

                            {isLoading ? (
                              <div className="mt-4 flex flex-wrap gap-3">
                                {Array.from({
                                  length:
                                    room.existing_room_images?.length || 4,
                                }).map((_, previewIndex) => (
                                  <div
                                    key={previewIndex}
                                    className="h-24 w-24 bg-gray-300 dark:bg-gray-700 rounded-lg animate-pulse"
                                  />
                                ))}
                              </div>
                            ) : (
                              <DragDropContext
                                onDragEnd={(result: any) => {
                                  const { source, destination } = result;
                                  if (!destination) return;

                                  const combinedImages = [
                                    ...(room.existing_room_images || []),
                                    ...(room.room_images || []),
                                  ];
                                  const reorderedImages = [...combinedImages];
                                  const [removed] = reorderedImages.splice(
                                    source.index,
                                    1
                                  );
                                  reorderedImages.splice(
                                    destination.index,
                                    0,
                                    removed
                                  );
                                  const newPosition = destination.index + 1;

                                  setReorderImages((prev: any) => {
                                    const updated = prev.filter(
                                      (item: any) =>
                                        item.position !== newPosition ||
                                        item.blob_id === removed.id
                                    );
                                    return [
                                      ...updated,
                                      {
                                        blob_id: removed.id,
                                        position: newPosition,
                                      },
                                    ];
                                  });

                                  const updatedExistingRoomImages =
                                    reorderedImages.filter(
                                      (item: any) => item.id
                                    );
                                  const updatedRoomImages =
                                    reorderedImages.filter(
                                      (item: any) => !item.id
                                    );

                                  setFieldValue(
                                    `rooms_attributes[${index}].existing_room_images`,
                                    updatedExistingRoomImages
                                  );
                                  setFieldValue(
                                    `rooms_attributes[${index}].room_images`,
                                    updatedRoomImages
                                  );
                                }}
                              >
                                <Droppable
                                  droppableId={`room-images-${index}`}
                                  direction="horizontal"
                                >
                                  {(provided: any) => (
                                    <div
                                      className="mt-4 flex flex-wrap gap-3"
                                      {...provided.droppableProps}
                                      ref={provided.innerRef}
                                    >
                                      {[
                                        ...(room.existing_room_images || []),
                                        ...(room.room_images || []),
                                      ].map(
                                        (image: any, previewIndex: number) => (
                                          <Draggable
                                            key={`image-${
                                              image.id || previewIndex
                                            }`}
                                            draggableId={`image-${
                                              image.id || previewIndex
                                            }`}
                                            index={previewIndex}
                                          >
                                            {(provided: any) => (
                                              <div
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                className="relative group"
                                              >
                                                <div className="h-24 w-24 rounded-lg overflow-hidden border-2 border-gray-200 dark:border-gray-700">
                                                  <NcImage
                                                    src={
                                                      image.url ||
                                                      URL.createObjectURL(image)
                                                    }
                                                    alt={`Room ${
                                                      index + 1
                                                    } Image ${
                                                      previewIndex + 1
                                                    }`}
                                                    className="w-full h-full object-cover"
                                                  />
                                                </div>
                                                <button
                                                  type="button"
                                                  onClick={() =>
                                                    handleRemoveRoomImage(
                                                      previewIndex,
                                                      index,
                                                      values,
                                                      setFieldValue
                                                    )
                                                  }
                                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-lg hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                                >
                                                  <svg
                                                    className="w-4 h-4"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                  >
                                                    <path
                                                      strokeLinecap="round"
                                                      strokeLinejoin="round"
                                                      strokeWidth={2}
                                                      d="M6 18L18 6M6 6l12 12"
                                                    />
                                                  </svg>
                                                </button>
                                              </div>
                                            )}
                                          </Draggable>
                                        )
                                      )}
                                      {provided.placeholder}
                                    </div>
                                  )}
                                </Droppable>
                              </DragDropContext>
                            )}
                          </div>

                          {/* Room Actions */}
                          <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                            {index > 0 && (
                              <button
                                type="button"
                                onClick={() => {
                                  setCount((prev) => prev - 1);
                                  setFieldValue(
                                    "rooms_attributes",
                                    values.rooms_attributes.filter(
                                      (_: any, idx: number) => idx !== index
                                    )
                                  );
                                }}
                                className="px-4 py-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors text-sm font-medium"
                              >
                                Remove Room
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Add Another Room Button */}
                {totalRooms && totalRooms > count && (
                  <button
                    type="button"
                    onClick={() => {
                      setCount((prev) => prev + 1);
                      setFieldValue("rooms_attributes", [
                        ...values.rooms_attributes,
                        initialRoomValues,
                      ]);
                      setExpandedRooms((prev) => ({ ...prev, [count]: true }));
                    }}
                    className="w-full py-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-2xl text-gray-600 dark:text-gray-400 hover:border-[#F56948] hover:text-[#F56948] transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                      />
                    </svg>
                    Add Another Room ({count}/{totalRooms})
                  </button>
                )}

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-6">
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Step 8 of 9 • Room details and images
                  </div>
                  <div className="flex gap-3">
                    <ButtonSecondary
                      href={`/add-listing-7/${slug}`}
                      disabled={isSubmitting}
                    >
                      <span className="flex items-center gap-2">
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 19l-7-7 7-7"
                          />
                        </svg>
                        Go Back
                      </span>
                    </ButtonSecondary>
                    <ButtonPrimary
                      type="button"
                      onClick={() => handleFormSubmit()}
                      disabled={isSubmitting}
                      className="min-w-[140px]"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center gap-2">
                          <svg
                            className="animate-spin h-4 w-4"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                              fill="none"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                          Saving...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          Continue
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </span>
                      )}
                    </ButtonPrimary>
                  </div>
                </div>
              </form>
            );
          }}
        </Formik>
      </div>
    </CommonLayoutV2>
  );
};

export default Rooms;